<template>
  <dialog
    class="SheetModal"
    :data-variant="variant"
    :data-fit="fit"
    :style="`--windowStartOffset: ${startOffset}`"
    open
  >
    <div
      class="SheetModal__backdrop"
      data-testid="backdrop"
      @click="$emit('close')"
    />

    <div class="SheetModal__window">
      <slot />
    </div>
  </dialog>
</template>

<script lang="ts" setup>
defineEmits<{
  close: [],
}>();

const {
  variant = "default",
  fit = "fill",
  startOffset = "0px",
} = defineProps<{
  fit?: "fill" | "hug",
  variant?: "default" | "custom",
  startOffset?: string,
}>();
</script>

<style lang="scss" scoped>
.SheetModal {
  --transitionEnter: 335ms cubic-bezier(0.25, 1, 0.30, 1);
  --transitionLeave: 280ms var(--easeInQuad);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  align-items: end;
  width: 100%;
  height: 100%;

  transition: none var(--transition);

  &.v-enter-active {
    --transition: var(--transitionEnter);
  }

  &.v-leave-active {
    --transition: var(--transitionLeave);
  }
}

.SheetModal__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  transition: 350ms background-color;

  cursor: pointer;

  .v-enter-active & {
    transition: 335ms cubic-bezier(0.35, 0.91, 0.33, 0.97);
    transition-property: opacity, background-color;
  }

  .v-leave-active & {
    transition: 250ms cubic-bezier(0.42, 0, 1, 1);
    transition-property: opacity, background-color;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.SheetModal__window {
  position: relative;
  max-height: calc(100% - var(--safeAreaInsetTop));
  transition: transform var(--transition);

  .v-enter-from &,
  .v-leave-to & {
    transform: translateY(calc(100% + var(--windowStartOffset)));
  }

  .SheetModal[data-variant="default"] & {
    border-radius: 16px 16px 0 0;
    background-color: var(--themeColorNeutral0);
  }

  .SheetModal[data-fit="fill"] & {
    height: 100%;
  }
}
</style>
